import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import cx from 'classnames';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { Container, Title, List, Item, Separator } from './styles';
import { useKeyNavigation } from '@spa/shared/hooks/keyNavigation';
import ArrowDown from '@spa/shared/svg/arrowDown';

const isActive = (selected, current) => selected.toUpperCase() === current.toUpperCase();

const Dropdown = ({ values, selected, onSelect, title, id, Icon }) => {
  const {
    i18n: { language }
  } = useTranslation('header');
  const [isOpen, setIsOpen] = useState(false);
  const htmlRef = useRef();
  const {
    isFocused,
    state: { selected: selectedIndex, blured }
  } = useKeyNavigation(htmlRef);
  const closeDropdown = () => setIsOpen(false);
  const toggleDropdown = useCallback(
    e => {
      setIsOpen(!isOpen);
    },
    [isOpen]
  );
  const delegateClickEvent = ({ target: { firstChild } }) => firstChild.click && firstChild.click();
  const onFocusHandler = () => setIsOpen(true);
  const closeDropdownAfter = handleFunction => e => {
    handleFunction(e);
    closeDropdown();
  };
  const { query } = useRouter();

  useEffect(() => {
    if (!blured && isFocused) {
      return;
    }

    closeDropdown();
  }, [blured, isFocused]);

  const itemContent = value => {
    if (value.link || value.href) {
      return (
        <Link {...value} href={value.href ? value.href(language) : value.link} query={query} prefetch={false}>
          {value.translation}
        </Link>
      );
    }

    return (
      <button value={value.key} onClick={closeDropdownAfter(onSelect)} readOnly>
        {value.translation}
      </button>
    );
  };

  const dropdownId = id || 'dropdown-checkbox';

  return (
    <Container ref={htmlRef} role="listbox" tabIndex="0" onFocus={onFocusHandler}>
      <input id={dropdownId} type="checkbox" checked={isOpen} readOnly className={'dropdown-checkbox'} />
      <Title onMouseDown={toggleDropdown} htmlFor={dropdownId}>
        {title ? title : selected}
        {Icon ? <Icon /> : <ArrowDown className={isOpen ? 'open' : ''} />}
      </Title>
      <List>
        {values.map((value, i) => (
          <Fragment key={i}>
            <Item
              role="option"
              tabIndex="-1"
              aria-selected={selectedIndex === i + 1}
              onClick={delegateClickEvent}
              className={cx({ selected: isActive(selected, value.translation) })}
            >
              {itemContent(value)}
            </Item>
            {value.separator ? <Separator /> : null}
          </Fragment>
        ))}
      </List>
    </Container>
  );
};

Dropdown.propTypes = {
  values: PropTypes.array.isRequired,
  selected: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
  id: PropTypes.string,
  Icon: PropTypes.node
};

export default Dropdown;
